import {
  JobCategory as JobCategoryComponent,
  type JobCategoryRef,
  type JobCategoryChangeHandler,
  type JobCategoryChangeEvent,
  OTHER_OPTION_ID,
} from '@seek/job-category';
import React, {
  forwardRef,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uid } from 'uuid';

import { JOB_CATEGORIES } from 'src/app/shared/constants/jobCategories';
import {
  CATEGORY_OTHER_PRESSED,
  CATEGORY_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import {
  type Classification,
  getClassificationDataBySeekIds,
} from 'src/app/utils/classifications';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateCategoryId } from 'src/app/v1/state/modules/formData/categoryId';
import { actionUpdateSubCategoryId } from 'src/app/v1/state/modules/formData/subCategoryId';
import { actionUpdateSuggestedCategoryId } from 'src/app/v1/state/modules/formData/suggestedCategoryId';
import { actionUpdateCategoryText } from 'src/app/v1/state/modules/session/categoryText';
import { actionUpdateInteractionId } from 'src/app/v1/state/modules/session/interactionId';
import { actionUpdateSeekClassificationId } from 'src/app/v1/state/modules/session/seekClassificationId';
import { actionUpdateSeekSubClassificationId } from 'src/app/v1/state/modules/session/seekSubClassificationId';
import { actionUpdateSubCategoryText } from 'src/app/v1/state/modules/session/subCategoryText';
import { actionUpdateAutoClassifyCategoryChosen } from 'src/app/v1/state/modules/tealium/autoClassifyCategoryChosen';
import { actionUpdateAutoClassifyCategorySuggested } from 'src/app/v1/state/modules/tealium/autoClassifyCategorySuggested';
import { actionUpdateJobAdClassification } from 'src/app/v1/state/modules/tealium/jobAdClassification';
import { actionUpdateJobAdSubClassification } from 'src/app/v1/state/modules/tealium/jobAdSubClassification';
import type { Category, CategorySuggestion } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { JobCategoryTaxonomy } from 'src/types/job-category';

import { getSupportedCategoryCountry } from './utils/getCategoryCountry';
import { formatJobCategoryTaxonomy } from './utils/taxonomy/formatTaxonomy';
import { getSubCategoryFromTaxonomy } from './utils/taxonomy/getSubCategoryFromTaxonomy';

const categoryList: JobCategoryTaxonomy[] =
  formatJobCategoryTaxonomy(JOB_CATEGORIES);

export const JobCategory = forwardRef((_props, ref: Ref<JobCategoryRef>) => {
  const {
    jobLocationCountry,
    jobTitle,
    nation,
    seekClassificationId,
    seekSubClassificationId,
  } = useSelector((state: AppState) => ({
    jobLocationCountry: state.session.jobLocationCountry,
    jobTitle: state.formData.jobTitle,
    nation: state.session.hirerAccount.nation.toLowerCase(),
    seekClassificationId: state.session.seekClassificationId,
    seekSubClassificationId: state.session.seekSubClassificationId,
  }));

  const dispatch = useDispatch();

  const [seekClsId, setseekClsId] = useState(seekClassificationId);
  const [seekSubClsId, setseekSubClsId] = useState(seekSubClassificationId);
  const previousTitle = useRef('');
  const { getLanguage } = useConfig();

  /* Reset categories on title change */
  useEffect(() => {
    if (jobTitle) {
      setseekClsId(seekClassificationId);
      setseekSubClsId(seekSubClassificationId);
      if (previousTitle.current && previousTitle.current !== jobTitle) {
        setseekClsId('');
        setseekSubClsId('');
      }
      previousTitle.current = jobTitle;
    }
  }, [jobTitle, seekClassificationId, seekSubClassificationId]);

  const concatenateValues = ({
    classificationId,
    subClassificationId,
  }: Classification) => `${classificationId}__${subClassificationId}`;

  const dispatchDataForSuggestedCategory = (
    category?: Category,
    isInManualSelectionMode?: boolean,
    suggestedCategoryList?: CategorySuggestion[],
  ) => {
    if (category) {
      if (
        category.subCategoryId !== OTHER_OPTION_ID ||
        !isInManualSelectionMode
      ) {
        const classification = getClassificationDataBySeekIds(
          category.categoryId,
          category.subCategoryId,
        );
        const concatenatedSuggestedCategory = concatenateValues(classification);
        dispatch(
          actionUpdateSuggestedCategoryId(concatenatedSuggestedCategory),
        );
        dispatch(actionUpdateCategoryId(classification.classificationId ?? ''));
        dispatch(
          actionUpdateSubCategoryId(classification.subClassificationId ?? ''),
        );
      } else {
        dispatch(
          actionUpdateSuggestedCategoryId(category.subCategoryId.toString()),
        );
        dispatch(actionUpdateCategoryId(''));
        dispatch(actionUpdateSubCategoryId(''));
      }

      if (suggestedCategoryList) {
        const suggestedCategoryListValues = suggestedCategoryList.map(
          (item: { category: Category }) => item.category,
        );

        const suggestedCategoryListLength = suggestedCategoryListValues.length;

        const selectedValuePosition =
          suggestedCategoryListValues.findIndex(
            (cat: Category) =>
              cat.categoryId === category.categoryId &&
              cat.subCategoryId === category.subCategoryId,
          ) + 1;

        const suggestedCategoryChosenValue =
          selectedValuePosition === 0 ? 'other' : selectedValuePosition;

        dispatch(
          actionUpdateAutoClassifyCategorySuggested(
            String(suggestedCategoryListLength),
          ),
        );
        dispatch(
          actionUpdateAutoClassifyCategoryChosen(
            String(suggestedCategoryChosenValue),
          ),
        );
      }

      dispatch(actionUpdateJobAdClassification(category.categoryName));
      dispatch(actionUpdateJobAdSubClassification(category.subCategoryName));
      dispatch(actionUpdateCategoryText(category.categoryName));
      dispatch(actionUpdateSubCategoryText(category.subCategoryName));
      dispatch(
        actionUpdateSeekClassificationId(category.categoryId.toString()),
      );
      dispatch(
        actionUpdateSeekSubClassificationId(category.subCategoryId.toString()),
      );
      dispatch(actionUpdateInteractionId(uid()));
    }
  };

  const handleCategoryChange: JobCategoryChangeHandler = ({
    category,
  }: JobCategoryChangeEvent) => {
    if (category) {
      // map seek classificationId to AdCentre categoryId
      const selectedAdCentreCategory = categoryList.find(
        (categoryTaxonomy: JobCategoryTaxonomy) =>
          categoryTaxonomy.seekId === category.categoryId.toString(),
      );
      dispatch(
        actionUpdateCategoryId(
          selectedAdCentreCategory ? selectedAdCentreCategory.value : '',
        ),
      );
      dispatch(actionUpdateCategoryText(category.categoryName));
      dispatch(
        actionUpdateSeekClassificationId(category.categoryId.toString()),
      );
      dispatch(actionUpdateJobAdClassification(category.categoryName));
    }
  };

  const handleSubCategoryChange: JobCategoryChangeHandler = ({
    category,
  }: JobCategoryChangeEvent) => {
    if (category) {
      // map seek subClassificationId to AdCentre subCategoryId
      const selectedAdCentreSubCategory = getSubCategoryFromTaxonomy(
        categoryList,
        category.categoryId.toString(),
        category.subCategoryId.toString(),
      );
      dispatch(
        actionUpdateSubCategoryId(
          selectedAdCentreSubCategory ? selectedAdCentreSubCategory.value : '',
        ),
      );
      dispatch(actionUpdateSubCategoryText(category.subCategoryName));
      dispatch(
        actionUpdateSeekSubClassificationId(category.subCategoryId.toString()),
      );
      dispatch(actionUpdateJobAdSubClassification(category.subCategoryName));
    }
  };

  const handleSuggestedCategoryChange: JobCategoryChangeHandler = ({
    isInManualSelectionMode,
    category,
    suggestedCategoryList,
  }: JobCategoryChangeEvent) => {
    if (category) {
      dispatchDataForSuggestedCategory(
        category,
        isInManualSelectionMode,
        suggestedCategoryList,
      );

      const objectInteraction = isInManualSelectionMode
        ? 'category-other'
        : `category-${findSuggestedCategoryPositionByValue(
            category,
            suggestedCategoryList,
          )}`;

      const eventData = {
        eventName: isInManualSelectionMode
          ? CATEGORY_OTHER_PRESSED
          : CATEGORY_PRESSED,
        objectInteraction,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };

  const findSuggestedCategoryPositionByValue = (
    cat: Category,
    suggestedCategoryList?: CategorySuggestion[],
  ) =>
    (suggestedCategoryList || [])
      // default to empty array as default return value is -1 is cannot find index
      .findIndex(
        ({ category: suggestedCategory }: CategorySuggestion) =>
          suggestedCategory.categoryId === cat.categoryId &&
          suggestedCategory.subCategoryId === cat.subCategoryId,
      ) + 1;

  const categoryCountry = Boolean(jobLocationCountry)
    ? jobLocationCountry
    : nation;

  return (
    <JobCategoryComponent
      country={getSupportedCategoryCountry(categoryCountry)}
      inputIdPrefix="classify"
      jobTitle={jobTitle}
      onCategoryChange={handleCategoryChange}
      onSubCategoryChange={handleSubCategoryChange}
      onSuggestedCategoryChange={handleSuggestedCategoryChange}
      preSelectedCategoryId={seekClsId}
      preSelectedSubCategoryId={seekSubClsId}
      ref={ref}
      language={getLanguage()}
    />
  );
});
